import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import request from "@/lib/api/handler";
import type { PayloadAction } from "@reduxjs/toolkit";
import networkErrorHandler from "../../utils/networkErrorHandler";
import {
  CandidateAcceptOpportunityData,
  CandidateDeclineOpportunityData,
  CandidateJobOpening,
  MinimalCandidateStatus,
} from "../../types";

// Define a type for the slice state
interface OpeningState {
  opening?: CandidateJobOpening;
  openings: CandidateJobOpening[];
  status: "idle" | "pending" | "succeeded" | "failed";
  postingStatus: "idle" | "posting" | "succeeded" | "failed";
}

// Define the initial state using that type
const initialState: OpeningState = {
  openings: [],
  status: "idle",
  postingStatus: "idle",
};

export const applyForOpening = createAsyncThunk(
  "candidate/openings/post",
  async (
    {
      openingId,
      data,
      token,
    }: {
      openingId: string;
      data: CandidateAcceptOpportunityData;
      token: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const candidateStatus = await request<MinimalCandidateStatus>({
        method: "POST",
        path: `/api/jobOpening/${openingId}/apply`,
        body: JSON.stringify(data),
        token,
      });

      return {
        id: openingId,
        candidateStatus,
      };
    } catch (error) {
      // TODO: Might not even need to bother with this, can just not catch and let errorSlice handle
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  }
);

export const withdrawApplicationForOpening = createAsyncThunk(
  "candidate/openings/post",
  async (
    {
      openingId,
      candidateStatusId,
      data,
      token,
    }: {
      openingId: string;
      candidateStatusId: string;
      data: CandidateDeclineOpportunityData;
      token: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const candidateStatus = await request<MinimalCandidateStatus>({
        method: "POST",
        path: `/api/jobOpening/${openingId}/candidate/${candidateStatusId}/withdraw`,
        body: JSON.stringify(data),
        token,
      });

      return {
        id: openingId,
        candidateStatus,
      };
    } catch (error) {
      // TODO: Might not even need to bother with this, can just not catch and let errorSlice handle
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  }
);

export const candidateOpeningSlice = createSlice({
  name: "candidateOpening",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    resetPostingStatus: (state) => {
      state.postingStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(applyForOpening.pending, (state) => {
        state.postingStatus = "posting";
      })
      .addCase(
        applyForOpening.fulfilled,
        (
          state,
          action: PayloadAction<Partial<CandidateJobOpening> | undefined>
        ) => {
          if (action.payload) {
            const openingIdx = state.openings.findIndex(
              ({ id }) => action.payload?.id === id
            );
            const opening = state.openings[openingIdx];

            if (opening) {
              const openings = [...state.openings];
              openings[openingIdx] = {
                ...opening,
                ...action.payload,
              };

              state.openings = openings;
            }
          }

          state.postingStatus = "succeeded";
        }
      )
      .addCase(applyForOpening.rejected, (state) => {
        state.postingStatus = "failed";
      });
  },
});

export const { resetPostingStatus } = candidateOpeningSlice.actions;

export default candidateOpeningSlice.reducer;
